import { Box, Popover, Typography } from '@mui/material';
import { ApolloError } from '@apollo/client';
import { anchorRef, bindPopover, bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  TableHeader,
  Pagination,
  FilterButton,
  LoadedContent,
  IPaginationProps,
  LinearProgress,
} from '@occc-app/design-system';

import { ExternalLoyaltyManualOffer, OfferTableType, OffersFilterValues } from '../../../tobacco-api-offers.types';

import { OffersTableRow } from './offers-table-row.component';
import { OffersFilterForm } from './offers-filter-form.component';

type OffersTableProps = {
  title: string;
  data?: ExternalLoyaltyManualOffer[];
  loading: boolean;
  error?: ApolloError;
  tableType: OfferTableType;
  paginationProps: IPaginationProps;
  filterValues: OffersFilterValues;
  isFiltered: boolean;
  onFilterApply: (values: OffersFilterValues) => void;
  onFilterReset: () => void;
  onOfferClick: (uuid: string) => void;
  onOfferCreate?: (uuid: string) => void;
  onOfferDelete?: (uuid: string) => void;
  actionLoadingUuid?: string;
};

export const OffersTable = ({
  title,
  data,
  loading,
  error,
  tableType,
  paginationProps,
  filterValues,
  isFiltered,
  onFilterApply,
  onFilterReset,
  onOfferClick,
  onOfferCreate,
  onOfferDelete,
  actionLoadingUuid,
}: OffersTableProps) => {
  const filterPopupState = usePopupState({
    variant: 'popover',
    popupId: `filter-${tableType}`,
  });

  const popoverProps = bindPopover(filterPopupState);

  const handleFilterClose = () => {
    popoverProps.onClose();
  };

  const renderActions = () => (
    <FilterButton
      {...bindTrigger(filterPopupState)}
      ref={anchorRef(filterPopupState)}
      showBadge={isFiltered}
    />
  );

  const renderData = (items: ExternalLoyaltyManualOffer[]) => (
    <>
      <Box sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: 1100 }}>
          <TableHead>
            <TableRow sx={{ whiteSpace: 'nowrap' }}>
              <TableCell>Offer Name</TableCell>
              <TableCell>Campaign ID</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Manufacturer</TableCell>
              <TableCell>States</TableCell>
              <TableCell>Stores</TableCell>
              <TableCell>Run Dates</TableCell>
              <TableCell>API Status</TableCell>
              <TableCell>Loyalty Host Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((offer) => (
              <OffersTableRow
                key={offer.uuid}
                offer={offer}
                onClick={onOfferClick}
                onCreateAction={onOfferCreate}
                onDeleteAction={onOfferDelete}
                actionLoadingUuid={actionLoadingUuid}
              />
            ))}
          </TableBody>
        </Table>
      </Box>
      <Pagination {...paginationProps} />
    </>
  );

  const renderTitle = () => (
    <Typography variant='subtitle1' component='h2' sx={{ whiteSpace: 'nowrap' }}>
      {title}
    </Typography>
  );

  return (
    <TableContainer>
      <TableHeader title={renderTitle()} disableTitleTypography actions={renderActions()} />

      {loading && <LinearProgress />}

      <LoadedContent<ExternalLoyaltyManualOffer>
        loading={loading}
        error={error}
        items={data}
        renderData={renderData}
      />

      <Popover
        {...popoverProps}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        keepMounted
        slotProps={{ paper: { sx: { width: 340 } } }}
      >
        <OffersFilterForm
          tableType={tableType}
          isOpen={filterPopupState.isOpen}
          initialValues={filterValues}
          onApply={onFilterApply}
          onReset={onFilterReset}
          onClose={handleFilterClose}
        />
      </Popover>
    </TableContainer>
  );
};
